import React, { useState, useEffect, useCallback } from "react";

import {
  HardDrive, Database, FileText, IndianRupee, Search,
  ChevronRight, AlertTriangle, TrendingUp, BarChart3,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import { PageHeader } from "../../ui";
import apiClient from "../../../utils/apiClient";
import "./sa-storage.scss";

const tooltipStyle = {
  backgroundColor: "var(--card-bg, #fff)",
  border: "1px solid var(--border-primary, #e5e7eb)",
  borderRadius: "8px",
  fontSize: "12px",
  padding: "8px 12px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  color: "var(--text-primary, #111827)",
};

const CATEGORY_COLORS = {
  videos: "#2563eb",
  recordings: "#7c3aed",
  materials: "#059669",
  thumbnails: "#d97706",
  assignments: "#0891b2",
  certificates: "#e11d48",
  chat: "#6366f1",
  branding: "#f59e0b",
  other: "#94a3b8",
};

const CATEGORY_LABELS = {
  videos: "Videos",
  recordings: "Recordings",
  materials: "Materials",
  thumbnails: "Thumbnails",
  assignments: "Assignments",
  certificates: "Certificates",
  chat: "Chat Files",
  branding: "Branding",
  other: "Other",
};



/* ─── Institute Detail Modal ─────────────────────────────────────────────── */
const InstituteDetailModal = ({ instituteId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!instituteId) return;
    (async () => {
      try {
        const res = await apiClient.get(`/sa/storage/institute/${instituteId}`);
        setData(res.data);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [instituteId]);

  if (!instituteId) return null;

  const catData = data?.categories
    ? Object.entries(data.categories)
        .filter(([, v]) => v.sizeBytes > 0)
        .map(([key, v]) => ({
          name: CATEGORY_LABELS[key] || key,
          value: v.sizeGB,
          bytes: v.sizeBytes,
          count: v.count,
          color: CATEGORY_COLORS[key] || "#94a3b8",
        }))
        .sort((a, b) => b.value - a.value)
    : [];

  return (
    <div className="sa-storage-overlay" onClick={onClose}>
      <div className="sa-storage-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sa-storage-modal__header">
          <div>
            <h3 className="sa-storage-modal__title">
              {loading ? "Loading…" : data?.institute?.name || "Institute"}
            </h3>
            {data?.institute?.subdomain && (
              <span className="sa-storage-modal__sub">{data.institute.subdomain}</span>
            )}
          </div>
          <button className="sa-storage-modal__close" onClick={onClose}>×</button>
        </div>

        {loading ? (
          <div className="sa-storage-modal__body sa-storage-modal__loading">
            <div className="spinner-border spinner-border-sm" />
          </div>
        ) : data ? (
          <div className="sa-storage-modal__body">
            {/* Usage Overview */}
            <div className="sa-storage-detail__overview">
              <div className="sa-storage-detail__meter">
                <div className="sa-storage-detail__meter-labels">
                  <span>{data.usage.usedGB} GB used</span>
                  <span>
                    {data.plan.isUnlimited ? "Unlimited" : `${data.usage.totalQuotaGB} GB total`}
                  </span>
                </div>
                <div className="sa-storage-detail__bar">
                  <div
                    className={`sa-storage-detail__bar-fill ${data.usage.usagePercent > 90 ? "danger" : data.usage.usagePercent > 70 ? "warning" : ""}`}
                    style={{ width: `${data.plan.isUnlimited ? 0 : data.usage.usagePercent}%` }}
                  />
                </div>
              </div>
              <div className="sa-storage-detail__stats">
                <div className="sa-storage-detail__stat">
                  <span className="sa-storage-detail__stat-value">{data.plan.name}</span>
                  <span className="sa-storage-detail__stat-label">Plan</span>
                </div>
                <div className="sa-storage-detail__stat">
                  <span className="sa-storage-detail__stat-value">{data.usage.fileCount.toLocaleString()}</span>
                  <span className="sa-storage-detail__stat-label">Files</span>
                </div>
                <div className="sa-storage-detail__stat">
                  <span className="sa-storage-detail__stat-value">{data.usage.extraStorageGB} GB</span>
                  <span className="sa-storage-detail__stat-label">Extra Storage</span>
                </div>
              </div>
            </div>

            {/* Category Breakdown */}
            {catData.length > 0 && (
              <div className="sa-storage-detail__section">
                <h4 className="sa-storage-detail__section-title">Storage by Category</h4>
                <div className="sa-storage-detail__categories">
                  {catData.map((cat) => (
                    <div key={cat.name} className="sa-storage-detail__cat-row">
                      <div className="sa-storage-detail__cat-info">
                        <span className="sa-storage-detail__cat-dot" style={{ background: cat.color }} />
                        <span className="sa-storage-detail__cat-name">{cat.name}</span>
                        <span className="sa-storage-detail__cat-count">{cat.count} files</span>
                      </div>
                      <span className="sa-storage-detail__cat-size">{cat.value} GB</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Monthly Trend */}
            {data.monthly?.length > 0 && (
              <div className="sa-storage-detail__section">
                <h4 className="sa-storage-detail__section-title">Monthly Activity</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={data.monthly} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary)" vertical={false} />
                    <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                    <YAxis axisLine={false} tickLine={false} tickFormatter={(v) => `${v}GB`} tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="uploadedGB" name="Uploaded" fill="#2563eb" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="deletedGB" name="Deleted" fill="#e11d48" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Purchases */}
            {data.purchases?.length > 0 && (
              <div className="sa-storage-detail__section">
                <h4 className="sa-storage-detail__section-title">Storage Purchases</h4>
                <table className="sa-mini-table">
                  <thead>
                    <tr>
                      <th>Storage</th>
                      <th>Amount</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.purchases.map((p, i) => (
                      <tr key={i}>
                        <td>{p.storageGB} GB</td>
                        <td>₹{p.amountRupees}</td>
                        <td>{new Date(p.activatedAt || p.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div className="sa-storage-modal__body sa-storage-modal__loading">
            <p>Failed to load data.</p>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Main SA Storage Page ───────────────────────────────────────────────── */
const SAStoragePage = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedInstitute, setSelectedInstitute] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [ovRes, trRes] = await Promise.all([
        apiClient.get("/sa/storage/overview"),
        apiClient.get("/sa/storage/monthly-trend"),
      ]);
      setOverview(ovRes.data);
      setTrend(trRes.data.trend || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totals = overview?.totals || {};
  const institutes = overview?.institutes || [];
  const filtered = institutes.filter(
    (i) => !search || i.name?.toLowerCase().includes(search.toLowerCase()) || i.subdomain?.toLowerCase().includes(search.toLowerCase())
  );

  // Aggregate category data for donut
  const categoryAgg = {};
  institutes.forEach((inst) => {
    if (inst.categories) {
      for (const [cat, data] of Object.entries(inst.categories)) {
        if (!categoryAgg[cat]) categoryAgg[cat] = 0;
        categoryAgg[cat] += data.bytes || 0;
      }
    }
  });
  const donutData = Object.entries(categoryAgg)
    .filter(([, bytes]) => bytes > 0)
    .map(([key, bytes]) => ({
      name: CATEGORY_LABELS[key] || key,
      value: Number((bytes / 1024 ** 3).toFixed(2)),
      color: CATEGORY_COLORS[key] || "#94a3b8",
    }))
    .sort((a, b) => b.value - a.value);

  if (loading) {
    return (
      <div className="sa-storage">
        <PageHeader title="Storage Management" description="Monitor storage usage across all institutes" icon={HardDrive} />
        <div className="sa-storage__loading"><div className="spinner-border" /></div>
      </div>
    );
  }

  return (
    <div className="sa-storage">
      <PageHeader title="Storage Management" description="Monitor storage usage across all institutes" icon={HardDrive} />

      {/* KPI Cards */}
      <div className="sa-grid-4">
        <div className="sa-kpi-card">
          <div className="sa-kpi-inner">
            <div>
              <div className="sa-kpi-label">Total Used</div>
              <div className="sa-kpi-value">{totals.totalUsedGB || 0} GB</div>
              <div className="sa-kpi-sub">{totals.totalFiles?.toLocaleString() || 0} files</div>
            </div>
            <div className="sa-kpi-icon blue"><HardDrive size={20} /></div>
          </div>
        </div>
        <div className="sa-kpi-card">
          <div className="sa-kpi-inner">
            <div>
              <div className="sa-kpi-label">Total Quota</div>
              <div className="sa-kpi-value">{totals.totalQuotaGB || 0} GB</div>
              <div className="sa-kpi-sub">Across all plans</div>
            </div>
            <div className="sa-kpi-icon emerald"><Database size={20} /></div>
          </div>
        </div>
        <div className="sa-kpi-card">
          <div className="sa-kpi-inner">
            <div>
              <div className="sa-kpi-label">Institutes</div>
              <div className="sa-kpi-value">{totals.instituteCount || 0}</div>
              <div className="sa-kpi-sub">Active institutes</div>
            </div>
            <div className="sa-kpi-icon violet"><FileText size={20} /></div>
          </div>
        </div>
        <div className="sa-kpi-card">
          <div className="sa-kpi-inner">
            <div>
              <div className="sa-kpi-label">Storage Revenue</div>
              <div className="sa-kpi-value">₹{((totals.storagePurchaseRevenue || 0) / 100).toLocaleString()}</div>
              <div className="sa-kpi-sub">Extra storage sales</div>
            </div>
            <div className="sa-kpi-icon amber"><IndianRupee size={20} /></div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="sa-grid-2-1">
        {/* Monthly Trend */}
        <div className="sa-card">
          <div className="sa-card-head">
            <div>
              <div className="sa-card-title">Monthly Upload Trend</div>
              <div className="sa-card-subtitle">Storage uploaded per month (last 12 months)</div>
            </div>
            <TrendingUp size={16} className="sa-card-icon" />
          </div>
          <div className="sa-card-body">
            {trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={trend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradStorage" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary)" vertical={false} />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
                  <YAxis axisLine={false} tickLine={false} tickFormatter={(v) => `${v}GB`} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} GB`]} />
                  <Area type="monotone" dataKey="uploadedGB" name="Uploaded" stroke="#2563eb" strokeWidth={2.5} fill="url(#gradStorage)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="sa-storage__empty-chart">No data yet</div>
            )}
          </div>
        </div>

        {/* Category Donut */}
        <div className="sa-card">
          <div className="sa-card-head">
            <div>
              <div className="sa-card-title">By Category</div>
              <div className="sa-card-subtitle">Storage distribution</div>
            </div>
            <BarChart3 size={16} className="sa-card-icon" />
          </div>
          <div className="sa-card-body">
            {donutData.length > 0 ? (
              <>
                <div className="sa-donut-wrapper">
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie
                        data={donutData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={72}
                        paddingAngle={3}
                        dataKey="value"
                        strokeWidth={0}
                      >
                        {donutData.map((d, i) => <Cell key={i} fill={d.color} />)}
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} GB`]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="sa-donut-center">
                    <span className="sa-donut-center__value">{totals.totalUsedGB || 0}</span>
                    <span className="sa-donut-center__label">Total GB</span>
                  </div>
                </div>
                <div className="sa-chart-legend">
                  {donutData.map((d) => (
                    <div key={d.name} className="sa-chart-legend__item">
                      <span className="sa-chart-legend__dot" style={{ background: d.color }} />
                      <span className="sa-chart-legend__label">{d.name}</span>
                      <span className="sa-chart-legend__count">{d.value} GB</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="sa-storage__empty-chart">No data yet</div>
            )}
          </div>
        </div>
      </div>

      {/* Institutes Table */}
      <div className="sa-card">
        <div className="sa-card-head">
          <div>
            <div className="sa-card-title">Institute Storage Usage</div>
            <div className="sa-card-subtitle">{institutes.length} institutes</div>
          </div>
          <div className="sa-storage__search">
            <Search size={14} />
            <input
              type="text"
              placeholder="Search institutes…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="sa-table-scroll">
          <table className="sa-mini-table">
            <thead>
              <tr>
                <th>Institute</th>
                <th>Plan</th>
                <th>Used</th>
                <th>Quota</th>
                <th style={{ minWidth: 140 }}>Usage</th>
                <th>Files</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="sa-storage__empty-row">No institutes found</td>
                </tr>
              ) : (
                filtered.map((inst) => (
                  <tr
                    key={inst._id}
                    className="sa-storage__inst-row"
                    onClick={() => setSelectedInstitute(inst._id)}
                  >
                    <td>
                      <div className="sa-storage__inst-name">{inst.name}</div>
                      {inst.subdomain && <div className="sa-storage__inst-sub">{inst.subdomain}</div>}
                    </td>
                    <td>
                      <span className="sa-storage__plan-badge">{inst.plan}</span>
                    </td>
                    <td className="sa-storage__mono">{inst.usedGB} GB</td>
                    <td className="sa-storage__mono">
                      {inst.isUnlimited ? "∞" : `${inst.quotaGB} GB`}
                    </td>
                    <td>
                      {!inst.isUnlimited ? (
                        <div className="sa-storage__usage-bar-wrapper">
                          <div className="sa-storage__usage-bar">
                            <div
                              className={`sa-storage__usage-bar-fill ${inst.usagePercent > 90 ? "danger" : inst.usagePercent > 70 ? "warning" : ""}`}
                              style={{ width: `${inst.usagePercent}%` }}
                            />
                          </div>
                          <span className="sa-storage__usage-pct">
                            {inst.usagePercent > 90 && <AlertTriangle size={12} />}
                            {inst.usagePercent}%
                          </span>
                        </div>
                      ) : (
                        <span className="sa-storage__unlimited-tag">Unlimited</span>
                      )}
                    </td>
                    <td className="sa-storage__mono">{inst.fileCount.toLocaleString()}</td>
                    <td>
                      <ChevronRight size={16} className="sa-storage__chevron" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Institute Detail Modal */}
      <InstituteDetailModal
        instituteId={selectedInstitute}
        onClose={() => setSelectedInstitute(null)}
      />
    </div>
  );
};

export default SAStoragePage;
